import { useCallback, useEffect, useState } from 'react';
import { fetchSupportedLanguages } from '../services/languageService';
import { buildLanguageList } from '../utils/languageList';
import { readCachedLanguages, writeCachedLanguages } from '../utils/languageListStorage';

/**
 * The languages offered in the switcher, loaded from GET /translation/v1/languages.
 *
 * Starts from the last answer saved in the browser (so a repeat visit shows the languages at
 * once), or English only if there is none, then asks the backend and replaces the list when it
 * answers. If the request fails, the list we already have stays and `status` is 'error'.
 *
 * @returns {{ languages: Array, status: 'loading'|'loaded'|'error', reload: () => void }}
 *          `loaded` means the list came from the backend on this visit
 */
export const useSupportedLanguages = () => {
    const [languages, setLanguages] = useState(() => buildLanguageList(readCachedLanguages() || []));
    const [status, setStatus] = useState('loading');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');

        fetchSupportedLanguages()
            .then((remoteLanguages) => {
                if (cancelled) return;
                const list = buildLanguageList(remoteLanguages);
                // An empty answer is treated as a failure: keep what we have.
                if (list.length <= 1) {
                    setStatus('error');
                    return;
                }
                writeCachedLanguages(remoteLanguages);
                setLanguages(list);
                setStatus('loaded');
            })
            .catch((error) => {
                if (cancelled) return;
                console.warn('[Translation] Could not load languages:', error.message);
                setStatus('error');
            });

        return () => {
            cancelled = true;
        };
    }, [attempt]);

    /** Asks the backend again (the switcher's Retry button). */
    const reload = useCallback(() => setAttempt((value) => value + 1), []);

    return { languages, status, reload };
};
